'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import supabase from '@/lib/supabase';
import useAuthStore from '@/store/useAuthStore';
import HomeworkWelcome from '@/components/HomeworkWelcome';
import FullScreenLoader from '@/components/FlowQuiz/FullScreenLoader';

interface HomeworkSharePageProps {
  shortCode: string;
}

export default function HomeworkSharePage({ shortCode }: HomeworkSharePageProps) {
  const router = useRouter();
  const { user, profile } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [homeworkData, setHomeworkData] = useState<{
    id: string;
    trackId: string;
    topicName?: string;
    trackName?: string;
    questionCount?: number;
    coverUrl?: string | null;
  } | null>(null);

  useEffect(() => {
    const handleSharedLink = async () => {
      try {
        console.log('[Homework Share] Starting to handle share link:', shortCode);
        console.log('[Homework Share] User logged in:', !!user);

        // Validate shortCode format
        if (!shortCode || typeof shortCode !== 'string' || shortCode.length < 4) {
          console.error('[Homework Share] Invalid shortCode format:', shortCode);
          setError('Invalid share code format');
          setLoading(false);
          return;
        }

        // Query the database to get homework share info with topic and track details
        console.log('[Homework Share] Querying database for shortCode:', shortCode);
        const { data, error } = await supabase
          .from('teacher_homework_shares')
          .select(`
            id,
            track_id,
            topic:music_quiz_topics(name, cover_url),
            track:music_quiz_tracks!teacher_homework_shares_track_id_fkey(name, segments)
          `)
          .eq('short_code', shortCode)
          .eq('is_active', true)
          .single();

        if (error) {
          if (error.code !== 'PGRST116') {
            console.error('[Homework Share] Error fetching homework share:', error?.message ?? error, 'code:', error?.code, 'shortCode:', shortCode);
          }
          setError('Invalid or expired share link');
          setLoading(false);
          return;
        }

        if (!data || !data.track_id) {
          console.error('[Homework Share] No data found for shortCode:', shortCode);
          setError('Share link not found or invalid');
          setLoading(false);
          return;
        }

        console.log('[Homework Share] Found homework share:', { id: data.id, track_id: data.track_id });

        // Extract homework information
        const topicData = Array.isArray(data.topic) ? data.topic[0] : data.topic;
        const trackData = Array.isArray(data.track) ? data.track[0] : data.track;
        const questionCount = trackData?.segments?.length || 0;

        setHomeworkData({
          id: data.id,
          trackId: data.track_id,
          topicName: topicData?.name,
          trackName: trackData?.name,
          questionCount,
          coverUrl: topicData?.cover_url,
        });

        // Stop loading - show welcome page
        setLoading(false);
      } catch (err) {
        console.error('[Homework Share] Error handling shared link:', err);
        setError('Failed to load shared homework');
        setLoading(false);
      }
    };

    handleSharedLink();
  }, [shortCode, user]);

  // Handler for when user clicks "Continue to Pengi.ai"
  const handleContinue = () => {
    if (!homeworkData) return;

    // Check if we're on flowquiz.ai domain
    const isFlowQuizDomain = typeof window !== 'undefined' &&
      window.location.hostname.includes('flowquiz.ai');

    const redirectPath = `/quest/flowquiz/player/${homeworkData.trackId}?hw=${homeworkData.id}`;

    // If on flowquiz.ai, always redirect to pengi.ai player
    // Player page will handle login/onboarding checks
    if (isFlowQuizDomain) {
      window.location.href = `https://pengi.ai${redirectPath}`;
      return;
    }

    // On pengi.ai or localhost, always go to player
    // Player page will handle login/onboarding checks
    router.push(redirectPath);
  };


  if (loading) {
    return (
      <FullScreenLoader
        spinnerColor="border-purple-600"
        text="Loading homework..."
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-xl text-red-500 mb-4">{error}</div>
          <button
            onClick={() => router.push('/quest')}
            className="px-6 py-3 rounded-lg font-semibold text-white shadow-md hover:scale-105 transition-all"
            style={{
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Show welcome page when homework data is loaded
  if (homeworkData) {
    return (
      <HomeworkWelcome
        homeworkInfo={{
          topicName: homeworkData.topicName,
          coverUrl: homeworkData.coverUrl,
        }}
        onContinue={handleContinue}
      />
    );
  }

  return null;
}
